import { roleAtLeast } from '@inou/shared';
import { useState } from 'react';

import { MonthPicker } from '@/components/layout/MonthPicker';
import { PageHeader } from '@/components/layout/PageHeader';
import { StateBlock } from '@/components/reports/StateBlock';
import { TimeLogCreateForm } from '@/components/timelogs/TimeLogCreateForm';
import { TimeLogGrid } from '@/components/timelogs/TimeLogGrid';
import { Button } from '@/components/ui/button';
import { matchesQuery, SearchInput } from '@/components/ui/search-input';
import { Segmented } from '@/components/ui/segmented';
import { Stat, StatRow } from '@/components/ui/stat';
import type { Me, Project, TimeLog } from '@/lib/api';
import { hours, monthLabel } from '@/lib/format';
import { useMonthParam, useQueryParam } from '@/lib/nav';
import { GRID_AXES, type GridAxis } from '@/lib/timelogs';
import { useApi } from '@/lib/useApi';

/**
 * 工数明細。
 *
 * 記録の主経路はあくまで MCP なので、この画面は**締めのときに直すため**にある。
 * 管理者は全員分、それ以外は自分の分だけが見える。
 * 新しく記録を足せるのは自分の分だけ (管理者も他人の分は作れない)。
 * MCP Connect プランは画面から足せず、訂正だけができる。
 *
 * 時系列に積んだ一覧だと 1 か月分で数百行になり追えないので、
 * 「メンバー × プロジェクト」を縦、日を横に取ったグリッドで見せる。
 */
export function TimeLogsPage({ me }: { me: Me }) {
  // 画面から記録を足せるのは管理画面フルのロールだけ。
  // さらに足せるのは自分の分だけなので、行の持ち主も見る。
  const isFull = roleAtLeast(me.user.role, 'member');
  const canCreate = (userId: string) => isFull && userId === me.user.id;

  // レポートから「この月のこのプロジェクト」を開けるよう、月と絞り込みは URL が持つ
  const [month, setMonth] = useMonthParam();
  const [query, setQuery] = useQueryParam();
  const [adding, setAdding] = useState(false);
  // 自分の行しか無い人に「人単位」を出しても 1 行に潰れるだけ
  const [axis, setAxis] = useState<GridAxis>(isFull ? 'member' : 'project');

  const { data, error, loading, reload } = useApi<{
    month: string;
    canEditOthers: boolean;
    timeLogs: TimeLog[];
  }>(`/api/time-logs?month=${month}`);
  const projects = useApi<{ projects: Project[] }>('/api/projects');

  const all = data?.timeLogs ?? [];
  // 絞り込みはグリッドに出ている軸 (メンバー・プロジェクト) だけで行う
  const logs = all.filter((log) => matchesQuery(query, log.userName, log.projectName));
  const total = logs.reduce((sum, log) => sum + log.hours, 0);
  const capitalized = logs.reduce((sum, log) => sum + (log.isCapitalizedLog ? log.hours : 0), 0);

  return (
    <>
      <PageHeader
        title="工数明細"
        subtitle={
          query.trim() === ''
            ? `${monthLabel(month)} / ${all.length} 件`
            : `${monthLabel(month)} / ${logs.length} / ${all.length} 件`
        }
        actions={
          <>
            <MonthPicker month={month} onChange={setMonth} />
            {isFull ? (
              <Button variant="primary" onClick={() => setAdding((value) => !value)}>
                {adding ? '閉じる' : '記録を追加'}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        {adding ? (
          <TimeLogCreateForm
            projects={projects.data?.projects ?? []}
            month={month}
            onCreated={() => {
              setAdding(false);
              reload();
            }}
          />
        ) : null}

        <StateBlock
          loading={loading && data === null}
          error={error}
          empty={all.length === 0}
          emptyMessage={`${monthLabel(month)}の記録はまだありません。`}
        >
          <StatRow className="grid-cols-1 [&>*+*]:border-t md:grid-cols-3 md:[&>*+*]:border-t-0">
            <Stat label="合計" value={hours(total)} />
            <Stat label="資産化" value={hours(capitalized)} tone="brand" />
            <Stat label="費用化" value={hours(total - capitalized)} />
          </StatRow>

          <div className="border border-line bg-surface-raised">
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder={isFull ? 'メンバー・プロジェクトで絞り込む' : 'プロジェクトで絞り込む'}
                className="w-96"
              />
              {isFull ? (
                <Segmented value={axis} onChange={setAxis} options={GRID_AXES} label="見る軸" />
              ) : null}
            </div>
            {logs.length === 0 ? (
              <p className="border-t border-line px-4 py-8 text-sm text-ink-subtle">
                「{query}」に一致する記録はありません。
              </p>
            ) : (
              <TimeLogGrid
                month={month}
                logs={logs}
                projects={projects.data?.projects ?? []}
                axis={axis}
                canCreate={canCreate}
                onChanged={reload}
              />
            )}
          </div>
        </StateBlock>

        <p className="text-xs leading-relaxed text-ink-subtle">
          記録は AI クライアント (MCP) から行います。この画面は記録した数字を直すためのものです。
          マスを押すと、その日の記録の時間・カテゴリ・プロジェクトをその場で直せます。
          新しく足せるのは自分の記録だけです。
          {'メモのある記録はマスの角に印が付き、ホバーやフォーカスで全文が読めます。'}
          資産化するかどうかはプロジェクトの設定と作業カテゴリからサーバーが決めるので、
          直接は指定できません。
        </p>
      </div>
    </>
  );
}
